import React from 'react';
import { Truck } from 'lucide-react';

/*
 * Shared visual shell for the auth screens (Login / Forgot / Reset password).
 * Pure presentation only — no state, no side effects.
 */

export const AUTH_BG = '/images/login-bg-truck.jpg';
export const AUTH_LOGO = '/images/essaar-logo-white.png';

// Reusable class strings so all auth screens look identical.
export const authInputClass =
  'login-input w-full rounded-xl border border-slate-200 bg-white text-[15px] sm:text-sm text-slate-800 transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none disabled:bg-slate-50';
export const authIconClass =
  'absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400 pointer-events-none';
export const authLabelClass = 'block text-xs font-semibold text-slate-600 mb-1.5';
export const authPrimaryBtnClass =
  '!min-h-[46px] !rounded-xl !border-transparent bg-gradient-to-r from-[#0a3be6] via-[#1677f2] to-[#12c2f3] font-semibold tracking-wider shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40';
export const authOutlineLinkClass =
  'inline-flex items-center justify-center gap-2 w-full min-h-[44px] px-4 rounded-xl border border-blue-200 bg-white text-sm font-semibold text-blue-700 hover:bg-blue-50/60 hover:border-blue-300 transition';
export const authEyeBtnClass =
  'absolute right-2.5 top-1/2 -translate-y-1/2 !p-1.5 !border-0 !text-blue-500 hover:!bg-blue-50';

const Footer = ({ className = '' }) => (
  <p className={`text-xs text-blue-100/70 ${className}`}>
    All Rights Reserved. Designed with passion by{' '}
    <span className="font-bold text-white">DIGIQIN</span>
  </p>
);

const AuthLayout = ({ title, subtitle, children }) => (
  <div
    className="auth-shell min-h-screen min-h-[100dvh] relative overflow-x-hidden bg-[#0b1f5c] lg:bg-[#eef4fd]"
    style={{ fontFamily: "Inter, 'Segoe UI', system-ui, sans-serif" }}
  >
    {/* ===== BACKGROUND IMAGE ===== */}
    <div
      className="fixed lg:absolute inset-0 bg-cover bg-[22%_center] lg:bg-[left_center] z-0"
      style={{ backgroundImage: `url('${AUTH_BG}')` }}
    />
    {/* readability wash: top-down on mobile, left-to-right on desktop */}
    <div className="fixed lg:absolute inset-0 z-0 pointer-events-none bg-gradient-to-b from-[#06143f]/80 via-[#06143f]/40 to-[#06143f]/70 lg:bg-gradient-to-r lg:from-[#06143f]/55 lg:via-transparent lg:to-transparent" />

    <div className="relative z-10 min-h-screen min-h-[100dvh] w-full grid grid-cols-1 content-start lg:content-normal lg:grid-cols-12">

      {/* ================= LEFT CONTENT ================= */}
      <div className="lg:col-span-7 flex flex-col justify-between px-5 sm:px-10 lg:px-16 xl:px-20 pt-[max(1.75rem,env(safe-area-inset-top))] lg:pt-12 pb-6 lg:pb-10 lg:min-h-screen">
        <div>
          <img
            src={AUTH_LOGO}
            alt="Essaar Logistics"
            className="h-9 sm:h-12 lg:h-14 w-auto select-none"
            draggable="false"
          />

          <div className="mt-6 lg:mt-10 lg:mt-14">
            <span className="inline-flex items-center gap-2 rounded-full border border-sky-300/40 bg-white/5 backdrop-blur-sm px-3 py-1 sm:px-3.5 sm:py-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.2em] text-white/90">
              <Truck className="w-3.5 h-3.5 text-sky-300" strokeWidth={2} />
              Partner Portal
            </span>

            <h1
              className="mt-4 lg:mt-5 text-[2rem] leading-[1.12] sm:text-5xl xl:text-6xl font-extrabold tracking-tight"
              style={{ fontFamily: "Poppins, 'Segoe UI', system-ui, sans-serif" }}
            >
              <span className="block text-white">Move Smarter.</span>
              <span className="block bg-gradient-to-r from-[#22d3ff] to-[#38a8ff] bg-clip-text text-transparent">
                Deliver Faster.
              </span>
            </h1>

            <p className="mt-3 lg:mt-5 max-w-md text-sm sm:text-base leading-relaxed text-blue-100/85">
              Your trusted logistics partner, connecting shipments, people and possibilities.
            </p>
          </div>
        </div>

        <Footer className="hidden lg:block mt-10" />
      </div>

      {/* ================= RIGHT CARD ================= */}
      <div className="lg:col-span-5 flex items-start lg:items-center justify-center px-4 sm:px-6 pb-6 lg:py-10 lg:pr-10">
        <div className="w-full max-w-[440px] rounded-3xl border border-white/80 bg-white/95 backdrop-blur-xl shadow-[0_24px_70px_-20px_rgba(15,50,140,0.35)] px-5 py-7 sm:px-10 sm:py-10">
          <h2 className="text-sm font-bold uppercase tracking-[0.14em] text-[#0b1f4d]">
            {title}
          </h2>
          <div className="mt-2.5 h-[3px] w-16 rounded-full bg-gradient-to-r from-[#1d4ed8] via-[#22b8f0] to-[#34d399]" />
          {subtitle && (
            <p className="mt-4 text-sm text-slate-500">{subtitle}</p>
          )}
          <div className={subtitle ? 'mt-6' : 'mt-7'}>{children}</div>
        </div>
      </div>

      <Footer className="lg:hidden text-center px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]" />
    </div>
  </div>
);

export default AuthLayout;
